import React from 'react';
import _ from 'lodash';
import { Badge } from '@wix/wix-base-ui';
import { translate } from '#packages/i18n';
import { compIcon, sections } from '#packages/util';
import experiment from 'experiment';
import {
  type PageCategoryWithSubPages,
  pageIsInLinkPanelBlackList,
} from '@/panels';

import { AUTOMATION_IDS } from './utils/automationIds';
import { TRANSLATIONS_MAP } from './utils/translations';
import type { IMenuConfig } from './utils/config.types';

import {
  WIX_BLOG,
  WIX_BOOKINGS,
  WIX_EVENTS,
  WIX_GROUPS,
  WIX_CHALLENGES,
  WIX_NEW_STORES,
  WIX_PORTFOLIO,
  RESTAURANTS,
  MEMBERS_AREA,
  WIX_HOTELS_RUNNER,
} from '@wix/app-definition-ids';

export interface GetActionsOptions {
  onAddPageClick: () => void;
  onAddLinkClick: () => void;
  onAddAnchorClick: () => void;
  onAddSectionClick: () => void;
  onAddAppPageClick: (appID: string) => () => void;
  onAddDropdownClick: () => void;
  onAddDynamicClick: () => void;
  onAddContainerClick: () => void;
  showDynamicPages: boolean;
  showSitePages?: boolean;
  showSubmenu?: boolean;
  showDropdownTitle?: boolean;
  showLinks?: boolean;
  isMembersMenu: boolean;
  isMembersLoginMenu: boolean;
  isInMultiLingualFlow: boolean;
  isMembersAreaV2Context: boolean;
}

export interface MenuAddItemAction {
  type?: string;
  title: React.ReactNode;
  symbolName: string;
  disabled?: boolean;
  onClick: () => void;
  automationId?: string;
  isShowTooltip?: boolean;
  tooltipContent?: React.ReactNode;
}

const checkForValidAppEntries = (
  verticalsPages: PageCategoryWithSubPages[],
  appID: string,
) => {
  const installedAppsIDs = verticalsPages.map(
    (verticalPage) => verticalPage.id,
  );
  const appIndex = installedAppsIDs.indexOf(appID);
  if (appIndex !== -1) {
    const nonBlackListedAppPages = verticalsPages[appIndex].pages.filter(
      (page) => !pageIsInLinkPanelBlackList(appID, page),
    );
    return nonBlackListedAppPages.length > 0;
  }
  return false;
};

type ActionDecoratorFactory = (
  options: GetActionsOptions,
) => (actions: MenuAddItemAction[]) => MenuAddItemAction[];

export const getAddAppsMenuItemsActions = (
  options: GetActionsOptions,
  verticalsPages: PageCategoryWithSubPages[],
) => {
  const { showDynamicPages } = options;
  const decorators: ActionDecoratorFactory[] = [];
  if (showDynamicPages) {
    decorators.push(withDynamicPages);
  }
  if (checkForValidAppEntries(verticalsPages, WIX_NEW_STORES)) {
    decorators.push(withStorePage);
  }
  if (checkForValidAppEntries(verticalsPages, WIX_BLOG)) {
    decorators.push(withBlogPage);
  }
  if (checkForValidAppEntries(verticalsPages, WIX_BOOKINGS)) {
    decorators.push(withBookingsPage);
  }
  if (checkForValidAppEntries(verticalsPages, WIX_PORTFOLIO)) {
    decorators.push(withPortfolioPage);
  }
  if (checkForValidAppEntries(verticalsPages, RESTAURANTS)) {
    decorators.push(withRestaurantPage);
  }
  if (checkForValidAppEntries(verticalsPages, MEMBERS_AREA)) {
    decorators.push(withAccountsPage);
  }
  if (checkForValidAppEntries(verticalsPages, WIX_HOTELS_RUNNER)) {
    decorators.push(withHotelPage);
  }
  if (checkForValidAppEntries(verticalsPages, WIX_EVENTS)) {
    decorators.push(withEventPage);
  }
  if (checkForValidAppEntries(verticalsPages, WIX_CHALLENGES)) {
    decorators.push(withProgramPage);
  }
  if (checkForValidAppEntries(verticalsPages, WIX_GROUPS)) {
    decorators.push(withGroupsPage);
  }

  const decoratorsWithOptions = decorators.map((decorator) =>
    decorator(options),
  );

  return _.flowRight(...decoratorsWithOptions)([]);
};

export const getAddMenuItemsActions = (
  options: GetActionsOptions,
  config?: IMenuConfig,
) => {
  const {
    showDynamicPages,
    isMembersMenu,
    isMembersLoginMenu,
    isInMultiLingualFlow,
    isMembersAreaV2Context,
  } = options;

  let decorators = [
    withSitePages,
    withLinks,
    withSection,
    withAnchor,
    withSubmenu,
    withDropdownTitle,
  ];

  if (config?.features.containers.enabled) {
    decorators.push(withContainer);
  }

  if (isMembersLoginMenu) {
    decorators = [isMembersAreaV2Context ? withLinks : withMemberPages];
  } else if (isMembersMenu) {
    decorators = isInMultiLingualFlow
      ? [withMemberPages]
      : [withMemberPages, withSubmenu];
  }

  if (
    showDynamicPages &&
    experiment.isOpen('se_quickAppPageLinksFromAddItemsMenu')
  ) {
    decorators.unshift(withDynamicPages);
  }

  const decoratorsWithOptions = decorators.map((decorator) =>
    decorator(options),
  );

  return _.flowRight(...decoratorsWithOptions)([]);
};

const withSitePages: ActionDecoratorFactory =
  ({ onAddPageClick, showSitePages = true }) =>
  (actions) => {
    if (!showSitePages) {
      return actions;
    }

    const title = translate(TRANSLATIONS_MAP.FOOTER.MENU_ACTIONS.SITE_PAGES);
    const action = {
      title,
      type: 'action',
      symbolName: compIcon.toWBU('Page'),
      automationId: AUTOMATION_IDS.FOOTER.ACTIONS.SITE_PAGES,
      onClick: onAddPageClick,
    };

    return [action, ...actions];
  };

const withDynamicPages: ActionDecoratorFactory =
  ({ onAddDynamicClick, showDynamicPages }) =>
  (actions) => {
    if (!showDynamicPages) {
      return actions;
    }

    const title = translate(TRANSLATIONS_MAP.FOOTER.MENU_ACTIONS.DYNAMIC_PAGES);
    const action = {
      title,
      type: 'action',
      symbolName: compIcon.toWBU('PageDynamic'),
      automationId: AUTOMATION_IDS.FOOTER.ACTIONS.DYNAMIC_PAGES,
      onClick: onAddDynamicClick,
    };

    return [action, ...actions];
  };

const withLinks: ActionDecoratorFactory =
  ({ onAddLinkClick, showLinks = true }) =>
  (actions) => {
    if (!showLinks) {
      return actions;
    }

    const title = translate(TRANSLATIONS_MAP.FOOTER.MENU_ACTIONS.LINK);
    const action = {
      title,
      type: 'action',
      symbolName: compIcon.toWBU('UrlLink'),
      automationId: AUTOMATION_IDS.FOOTER.ACTIONS.LINK,
      onClick: onAddLinkClick,
    };

    return [action, ...actions];
  };

const withAnchor: ActionDecoratorFactory =
  ({ onAddAnchorClick }) =>
  (actions) => {
    const title = translate(TRANSLATIONS_MAP.FOOTER.MENU_ACTIONS.ANCHOR);
    const action = {
      title,
      type: 'action',
      symbolName: compIcon.toWBU('Anchor'),
      automationId: AUTOMATION_IDS.FOOTER.ACTIONS.ANCHOR,
      onClick: onAddAnchorClick,
    };

    return [action, ...actions];
  };

const withSection: ActionDecoratorFactory =
  ({ onAddSectionClick }) =>
  (actions) => {
    if (sections.isSectionsEnabled()) {
      const title = translate(TRANSLATIONS_MAP.FOOTER.MENU_ACTIONS.SECTION);
      const action = {
        title,
        type: 'action',
        symbolName: compIcon.toWBU('Section'),
        automationId: AUTOMATION_IDS.FOOTER.ACTIONS.SECTION,
        onClick: onAddSectionClick,
      };

      return [action, ...actions];
    }

    return actions;
  };

const withSubmenu: ActionDecoratorFactory =
  ({ onAddDropdownClick, showSubmenu = true }) =>
  (actions) => {
    if (!showSubmenu) {
      return actions;
    }

    const title = translate(TRANSLATIONS_MAP.FOOTER.MENU_ACTIONS.DROPDOWN);
    const action = {
      title,
      type: 'action',
      symbolName: compIcon.toWBU('MenuFolder'),
      automationId: AUTOMATION_IDS.FOOTER.ACTIONS.SUBMENU,
      onClick: onAddDropdownClick,
    };

    return [action, ...actions];
  };

const withDropdownTitle: ActionDecoratorFactory =
  ({ onAddDropdownClick, showDropdownTitle }) =>
  (actions) => {
    if (!showDropdownTitle) {
      return actions;
    }

    const action = {
      title: translate(TRANSLATIONS_MAP.FOOTER.MENU_ACTIONS.DROPDOWN_TITLE),
      type: 'action',
      symbolName: compIcon.toWBU('MenuFolder'),
      automationId: AUTOMATION_IDS.FOOTER.ACTIONS.SUBMENU,
      onClick: onAddDropdownClick,
    };

    return [action, ...actions];
  };

const withStorePage: ActionDecoratorFactory =
  ({ onAddAppPageClick }) =>
  (actions) => {
    const action = {
      title: translate('NewPages_Panel_AddMenuItem_MenuItems_Store_Page_Label'),
      type: 'action',
      symbolName: compIcon.toWBU('WixStores'),
      automationId: AUTOMATION_IDS.FOOTER.ACTIONS.STORE_PAGE,
      onClick: onAddAppPageClick(WIX_NEW_STORES),
    };

    return [action, ...actions];
  };

const withBlogPage: ActionDecoratorFactory =
  ({ onAddAppPageClick }) =>
  (actions) => {
    const action = {
      title: translate('NewPages_Panel_AddMenuItem_MenuItems_Blog_Page_Label'),
      type: 'action',
      symbolName: compIcon.toWBU('WixBlog'),
      automationId: AUTOMATION_IDS.FOOTER.ACTIONS.BLOG_PAGE,
      onClick: onAddAppPageClick(WIX_BLOG),
    };

    return [action, ...actions];
  };

const withBookingsPage: ActionDecoratorFactory =
  ({ onAddAppPageClick }) =>
  (actions) => {
    const action = {
      title: translate(
        'NewPages_Panel_AddMenuItem_MenuItems_Bookings_Page_Label',
      ),
      type: 'action',
      symbolName: compIcon.toWBU('WixBookings'),
      automationId: AUTOMATION_IDS.FOOTER.ACTIONS.BOOKINGS_PAGE,
      onClick: onAddAppPageClick(WIX_BOOKINGS),
    };

    return [action, ...actions];
  };

const withPortfolioPage: ActionDecoratorFactory =
  ({ onAddAppPageClick }) =>
  (actions) => {
    const action = {
      title: translate(
        'NewPages_Panel_AddMenuItem_MenuItems_Portfolio_Page_Label',
      ),
      type: 'action',
      symbolName: compIcon.toWBU('Page'),
      automationId: AUTOMATION_IDS.FOOTER.ACTIONS.PORTFOLIO_PAGE,
      onClick: onAddAppPageClick(WIX_PORTFOLIO),
    };

    return [action, ...actions];
  };

const withRestaurantPage: ActionDecoratorFactory =
  ({ onAddAppPageClick }) =>
  (actions) => {
    const action = {
      title: translate(
        'NewPages_Panel_AddMenuItem_MenuItems_Restaurant_Page_Label',
      ),
      type: 'action',
      symbolName: compIcon.toWBU('WixRestaurants'),
      automationId: AUTOMATION_IDS.FOOTER.ACTIONS.RESTAURANT_PAGE,
      onClick: onAddAppPageClick(RESTAURANTS),
    };

    return [action, ...actions];
  };

const withProgramPage: ActionDecoratorFactory =
  ({ onAddAppPageClick }) =>
  (actions) => {
    const action = {
      title: translate(
        'NewPages_Panel_AddMenuItem_MenuItems_Program_Page_Label',
      ),
      type: 'action',
      symbolName: compIcon.toWBU('AppMarket'),
      automationId: AUTOMATION_IDS.FOOTER.ACTIONS.PROGRAM_PAGE,
      onClick: onAddAppPageClick(WIX_CHALLENGES),
    };

    return [action, ...actions];
  };

const withGroupsPage: ActionDecoratorFactory =
  ({ onAddAppPageClick }) =>
  (actions) => {
    const action = {
      title: translate(
        'NewPages_Panel_AddMenuItem_MenuItems_Groups_Page_Label',
      ),
      type: 'action',
      symbolName: compIcon.toWBU('AppMarket'),
      automationId: AUTOMATION_IDS.FOOTER.ACTIONS.GROUPS_PAGE,
      onClick: onAddAppPageClick(WIX_GROUPS),
    };

    return [action, ...actions];
  };

const withEventPage: ActionDecoratorFactory =
  ({ onAddAppPageClick }) =>
  (actions) => {
    const action = {
      title: translate('NewPages_Panel_AddMenuItem_MenuItems_Event_Page_Label'),
      type: 'action',
      symbolName: compIcon.toWBU('AppMarket'),
      automationId: AUTOMATION_IDS.FOOTER.ACTIONS.EVENT_PAGE,
      onClick: onAddAppPageClick(WIX_EVENTS),
    };

    return [action, ...actions];
  };

const withAccountsPage: ActionDecoratorFactory =
  ({ onAddAppPageClick }) =>
  (actions) => {
    const action = {
      title: translate(
        'NewPages_Panel_AddMenuItem_MenuItems_Member_Page_Label',
      ),
      type: 'action',
      symbolName: compIcon.toWBU('Users'),
      automationId: AUTOMATION_IDS.FOOTER.ACTIONS.ACCOUNT_PAGE,
      onClick: onAddAppPageClick(MEMBERS_AREA),
    };

    return [action, ...actions];
  };

const withHotelPage: ActionDecoratorFactory =
  ({ onAddAppPageClick }) =>
  (actions) => {
    const action = {
      title: translate('NewPages_Panel_AddMenuItem_MenuItems_Hotel_Page_Label'),
      type: 'action',
      symbolName: compIcon.toWBU('WixHotels'),
      automationId: AUTOMATION_IDS.FOOTER.ACTIONS.HOTEL_PAGE,
      onClick: onAddAppPageClick(WIX_HOTELS_RUNNER),
    };

    return [action, ...actions];
  };

const withContainer: ActionDecoratorFactory =
  ({ onAddContainerClick }) =>
  (actions) => {
    const title = (
      <>
        {translate(TRANSLATIONS_MAP.FOOTER.MENU_ACTIONS.CONTAINER)}
        <Badge
          skin="standard"
          className="menu-manager-panel__add-container-badge"
          value={TRANSLATIONS_MAP.FOOTER.MENU_ACTIONS.CONTAINER_BADGE}
        />
      </>
    );

    const action: MenuAddItemAction = {
      title,
      type: 'action',
      symbolName: 'containerPageType',
      automationId: AUTOMATION_IDS.FOOTER.ACTIONS.CONTAINER,
      onClick: onAddContainerClick,
    };

    return [action, ...actions];
  };

const withMemberPages: ActionDecoratorFactory =
  ({ onAddPageClick }) =>
  (actions) => {
    const title = translate(TRANSLATIONS_MAP.FOOTER.MENU_ACTIONS.MEMBER_PAGES);
    const action = {
      title,
      type: 'action',
      symbolName: 'membersAction',
      automationId: AUTOMATION_IDS.FOOTER.ACTIONS.MEMBER_PAGES,
      onClick: onAddPageClick,
    };

    return [action, ...actions];
  };
